import { useState, useEffect, useCallback } from 'react';
import { getFirebaseToken as getToken } from '../../utils/getFirebaseToken';
import { notesService } from '../../services/notes.service';

/**
 * Shared state/logic behind NotesPanel, extracted so a caller that needs the
 * pen-icon trigger and the expanded note list in two different places in the
 * DOM (e.g. TransactionTimeline.js's action row vs. its own full-width block
 * below, mirroring how "Documents (N)" already breaks onto its own line)
 * can share one instance instead of fetching/tracking state twice.
 *
 * NotesPanel itself is unchanged for every other caller (Property, Documents,
 * Parties, Conversations) — it just calls this hook internally now.
 */
export function useNotesPanel({ linkType, linkId, linkLabel, transactionId, transactionName }) {
  const [notes, setNotes] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftReminder, setDraftReminder] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!linkType || !linkId) return;
    try {
      const token = await getToken();
      if (!token) return;
      // transactionId scopes the lookup to this transaction — required for
      // TRANSACTION-linked entities, since linkId alone (a prospect-stage or
      // active-listing-stage id) can be the same across different
      // transactions that share a stage template. See NoteRepository's
      // findByUserIdAndLinkTypeAndLinkId javadoc (rialty-user-service).
      const list = await notesService.listNotesByLink(token, linkType, linkId, transactionId);
      setNotes(list);
    } catch (e) {
      console.error('[useNotesPanel] Failed to load notes', e);
    }
  }, [linkType, linkId, transactionId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const count = notes.length;

  const openComposer = () => {
    setDraftText('');
    setDraftReminder('');
    setComposerOpen(true);
    setExpanded(true);
  };

  const saveComposer = async () => {
    if (!draftText.trim() || saving) return;
    setSaving(true);
    try {
      const token = await getToken();
      const created = await notesService.createNote(token, {
        text: draftText.trim(),
        link: { linkType, linkId, linkLabel },
        reminderDate: draftReminder || undefined,
        transactionId,
        transactionName,
      });
      setNotes((prev) => [created, ...prev]);
      setComposerOpen(false);
      setDraftText('');
      setDraftReminder('');
    } catch (e) {
      console.error('[useNotesPanel] Failed to create note', e);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (noteId, patch) => {
    const token = await getToken();
    const updated = await notesService.updateNote(token, noteId, patch);
    setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
  };

  const handleDelete = async (noteId) => {
    const token = await getToken();
    await notesService.deleteNote(token, noteId);
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
  };

  return {
    notes, count, expanded, setExpanded, composerOpen, setComposerOpen,
    draftText, setDraftText, draftReminder, setDraftReminder, saving,
    openComposer, saveComposer, handleUpdate, handleDelete,
  };
}
